import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Book, ArrowLeft, Calendar, Clock, Play, Pause, Volume2, Loader2, Music, Heart, Edit2, Save, X, Bookmark, RotateCcw, Mic, Glasses } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { Story } from '@shared/schema';
import { AudioLoadingAnimation } from '@/components/AudioLoadingAnimation';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { detectStoryMoments, shouldTriggerSound, type StoryMoment } from '@/utils/storyMomentDetector';
import { useToast } from '@/hooks/use-toast';
import { MerchandiseCatalog } from '@/components/MerchandiseCatalog';
import { StickerGenerator } from '@/components/StickerGenerator';
import { ShareStory } from '@/components/ShareStory';
import { CommunityShareButton } from '@/components/CommunityShareButton';
import { PdfBookDownload } from '@/components/PdfBookDownload';
import { PrintBookOrder } from '@/components/PrintBookOrder';
import { VoiceAssignmentDialog } from '@/components/VoiceAssignmentDialog';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Navigation from '@/components/Navigation';

export default function StoryPage() {
  const { id } = useParams<{ id: string }>();
  const storyId = Number(id);
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Validate storyId early - ensure it's a proper boolean
  const isValidStoryId = Number.isInteger(storyId) && storyId > 0;
  
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  
  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioText, setAudioText] = useState<string>('');
  const [highlightingEnabled, setHighlightingEnabled] = useState(true);
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(true);
  const [storyMoments, setStoryMoments] = useState<StoryMoment[]>([]);
  const [lastSoundIndex, setLastSoundIndex] = useState(-10);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isRestoringProgress, setIsRestoringProgress] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);
  
  const soundEffects = useSoundEffects();

  // Utility function to wait for all images and dynamic content to load
  const waitForContentLoad = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      // Check if images are still loading
      const images = document.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to prevent hanging
        });
      });

      // Wait for any pending DOM updates
      const domUpdatePromise = new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

      Promise.all([...imagePromises, domUpdatePromise]).then(() => {
        // Give a small buffer for any final layout shifts
        setTimeout(() => resolve(), 100);
      });
    });
  }, []);

  // Improved scroll position calculation with bounds checking
  const calculateScrollPosition = useCallback(async (scrollPercentage: number): Promise<number> => {
    // Wait for content to load first
    await waitForContentLoad();
    
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const maxScrollTop = Math.max(0, documentHeight - viewportHeight);
    
    // Calculate target scroll position
    const targetScroll = (maxScrollTop * scrollPercentage) / 100;
    
    // Bounds checking
    const validatedScroll = Math.max(0, Math.min(targetScroll, maxScrollTop));
    
    // Additional validation - ensure we're not trying to scroll to impossible positions
    if (documentHeight <= viewportHeight) {
      return 0; // No scrolling needed if content fits in viewport
    }
    
    return validatedScroll;
  }, [waitForContentLoad]);

  // Enhanced scroll restoration function
  const restoreScrollPosition = useCallback(async (scrollPercentage: number, smooth: boolean = true) => {
    try {
      const targetScroll = await calculateScrollPosition(scrollPercentage);
      
      // Scroll with behavior option
      window.scrollTo({ 
        top: targetScroll, 
        behavior: smooth ? 'smooth' : 'instant' 
      });
      
      return true; // Success
    } catch (error) {
      console.warn('Failed to restore scroll position:', error);
      return false; // Failed
    }
  }, [calculateScrollPosition]);
  
  // Fetch story data
  const { data: story, isLoading } = useQuery({
    queryKey: [`/api/stories/${storyId}`],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/stories/${storyId}`);
      const data = await response.json();
      return data.story as Story;
    },
    enabled: isValidStoryId,
    placeholderData: (prev) => prev
  });
  
  // Fetch favorites
  const { data: favorites } = useQuery({
    queryKey: ['/api/favorites'],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/favorites');
      return response.json();
    },
    placeholderData: (prev) => prev
  });
  
  const favoriteIds = favorites?.favoriteIds || [];

  // Fetch reading progress
  const { data: readingProgress } = useQuery({
    queryKey: [`/api/stories/${storyId}/progress`],
    enabled: isValidStoryId && !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/stories/${storyId}/progress`);
      return response.json();
    },
    placeholderData: (prev) => prev
  });

  // Save reading progress mutation
  const saveProgressMutation = useMutation({
    mutationFn: async (progress: {
      characterPosition: number;
      wordIndex: number;
      scrollPercentage: number;
    }) => {
      const response = await apiRequest('POST', `/api/stories/${storyId}/progress`, progress);
      return response.json();
    },
    onError: (error: Error) => {
      console.error('Failed to save reading progress:', error);
    }
  });
  
  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (storyId: number) => {
      const response = await apiRequest('POST', `/api/favorites/${storyId}/toggle`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favorites'] });
    }
  });
  
  // Update title mutation
  const updateTitleMutation = useMutation({
    mutationFn: async ({ id, title }: { id: number; title: string }) => {
      const response = await apiRequest('PATCH', `/api/stories/${id}/title`, { title });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/stories/${storyId}`] });
      setEditingTitle(false);
      toast({
        title: "Title updated",
        description: "Your story title has been saved.",
      });
    }
  });
  
  useEffect(() => {
    if (story) {
      setTitleValue(story.title || 'My Bedtime Story');
      const moments = detectStoryMoments(story.content);
      setStoryMoments(moments);
    }
  }, [story]);

  // Restore reading progress when data loads
  useEffect(() => {
    if (readingProgress?.progress && story && !isRestoringProgress) {
      setIsRestoringProgress(true);
      const progress = readingProgress.progress;
      
      // Restore word position for highlighting
      if (progress.wordIndex >= 0) {
        setCurrentWordIndex(progress.wordIndex);
      }
      
      // Restore scroll position with improved calculation
      if (progress.scrollPercentage > 0) {
        // Use improved scroll restoration with proper content loading detection
        restoreScrollPosition(progress.scrollPercentage)
          .then((success) => {
            if (success) {
              setScrollPosition(progress.scrollPercentage);
              console.log(`Successfully restored scroll position to ${Math.round(progress.scrollPercentage)}%`);
            } else {
              // Fallback to simple percentage-based scrolling
              console.warn('Enhanced scroll restoration failed, using fallback method');
              setTimeout(() => {
                const documentHeight = document.documentElement.scrollHeight;
                const viewportHeight = window.innerHeight;
                const maxScrollTop = Math.max(0, documentHeight - viewportHeight);
                const targetScroll = Math.max(0, Math.min((maxScrollTop * progress.scrollPercentage) / 100, maxScrollTop));
                
                window.scrollTo({ top: targetScroll, behavior: 'smooth' });
                setScrollPosition(progress.scrollPercentage);
              }, 200);
            }
          })
          .finally(() => {
            setIsRestoringProgress(false);
          });
      } else {
        setIsRestoringProgress(false);
      }
    }
  }, [readingProgress, story, restoreScrollPosition]);

  // Save progress periodically during reading
  useEffect(() => {
    if (!user || !story || isRestoringProgress) return;

    const saveProgress = () => {
      const scrollPercentage = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      
      // Calculate character position based on current word index
      const words = story.content.split(/\s+/);
      let characterPosition = 0;
      for (let i = 0; i < Math.min(currentWordIndex, words.length); i++) {
        characterPosition += words[i].length + 1; // +1 for space
      }

      const progress = {
        characterPosition: Math.max(0, characterPosition),
        wordIndex: Math.max(0, currentWordIndex),
        scrollPercentage: Math.max(0, Math.min(100, scrollPercentage))
      };

      // Only save if there's meaningful progress
      if (progress.wordIndex > 0 || progress.scrollPercentage > 5) {
        saveProgressMutation.mutate(progress);
      }
    };

    // Save progress every 10 seconds during audio playback
    let progressInterval: NodeJS.Timeout;
    if (isPlaying && currentWordIndex >= 0) {
      progressInterval = setInterval(saveProgress, 10000);
    }

    // Save progress on scroll (throttled)
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(saveProgress, 2000);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [user, story, isPlaying, currentWordIndex, isRestoringProgress]);
  
  // Helper function to clean story content for audio (remove image paths)
  const cleanTextForAudio = (text: string) => {
    // Remove image paths in both formats: ![alt](path) and (/objects/uploads/...)
    return text
      .replace(/\!\[.*?\]\([^)]*\)/g, '') // Remove markdown images
      .replace(/\([\/]?objects\/uploads\/[^)]+\)/g, '') // Remove path-only images
      .replace(/\s+/g, ' ') // Clean up multiple spaces
      .trim();
  };

  // Audio playback functions
  const generateAudio = async () => {
    console.log('generateAudio called', { story: story?.id, contentLength: story?.content?.length });
    if (!story) return;
    
    setIsLoadingAudio(true);
    setAudioUrl(null);
    
    try {
      // Clean the story content for audio generation (remove image paths)
      const cleanedContent = cleanTextForAudio(story.content);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const response = await fetch('/api/generate-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: cleanedContent,
          voice: 'nova',
          speed: 0.9,
          storyId: story.id
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error('Failed to generate audio');
      
      const audioBlob = await response.blob();
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      setAudioText(cleanedContent); // Store cleaned text for audio tracking
      
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.load();
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      
      let errorMessage = "Please try again later.";
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = "Audio generation timed out. Please try again.";
        } else if (error.message.includes('Failed to generate audio')) {
          errorMessage = "Audio generation failed. Please check your connection and try again.";
        }
      }
      
      toast({
        title: "Audio generation failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };
  
  const togglePlayPause = async () => {
    console.log('togglePlayPause called', { audioRef: audioRef.current, audioUrl, isPlaying });
    
    if (!audioRef.current) {
      console.log('audioRef.current is null, returning');
      return;
    }
    
    if (!audioUrl) {
      console.log('No audioUrl, generating audio');
      await generateAudio();
      return;
    }
    
    if (isPlaying) {
      console.log('Pausing audio');
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      console.log('Playing audio');
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Playback failed:', error);
        await generateAudio();
      }
    }
  };
  
  // Create word mapping between cleaned audio and original display text
  const createWordMapping = useCallback(() => {
    if (!story || !audioText) return [];
    
    const cleanedWords = audioText.split(/\s+/).filter(w => w.length > 0);
    const originalParts = story.content.split(/(\!\[.*?\]\(.*?\)|\([\/]?objects\/uploads\/[^)]+\))/);
    const mapping = [];
    let displayWordIndex = 0;
    
    // Build mapping of audio word index to display word index
    let audioWordIndex = 0;
    
    for (const part of originalParts) {
      // Skip image parts
      if (part.match(/\!\[(.*?)\]\((.*?)\)/) || part.match(/\(([\/]?objects\/uploads\/[^)]+)\)/)) {
        continue;
      }
      
      // Process text parts
      const words = part.trim().split(/\s+/).filter(w => w.length > 0);
      for (const word of words) {
        if (audioWordIndex < cleanedWords.length) {
          mapping[audioWordIndex] = displayWordIndex;
          audioWordIndex++;
        }
        displayWordIndex++;
      }
    }
    
    return mapping;
  }, [story?.content, audioText]);

  const wordMapping = useMemo(() => createWordMapping(), [createWordMapping]);

  // Auto-scroll to highlighted word - only when word is off-screen
  useEffect(() => {
    if (highlightingEnabled && currentWordIndex >= 0 && audioText) {
      const displayIndex = wordMapping[currentWordIndex];
      if (displayIndex !== undefined) {
        const wordElement = document.querySelector(`[data-word-index="${displayIndex}"]`) as HTMLElement;
        if (wordElement) {
          const rect = wordElement.getBoundingClientRect();
          const isVisible = (
            rect.top >= 0 &&
            rect.bottom <= window.innerHeight &&
            rect.left >= 0 &&
            rect.right <= window.innerWidth
          );
          
          // Only scroll if word is not visible in viewport
          if (!isVisible) {
            wordElement.scrollIntoView({
              behavior: 'auto', // Changed from 'smooth' to prevent animation conflicts
              block: 'nearest', // Changed from 'center' to be less aggressive
              inline: 'nearest'
            });
          }
        }
      }
    }
  }, [currentWordIndex, highlightingEnabled, audioText, wordMapping]);

  const renderHighlightedStory = () => {
    if (!story) return null;
    
    const savedWordIndex = readingProgress?.progress?.wordIndex || -1;
    
    // If highlighting is disabled or no audio text is loaded, render as markdown
    if (!highlightingEnabled || !audioText) {
      return (
        <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ node, ...props }) => (
                <img
                  {...props}
                  className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg"
                  style={{ display: 'block' }}
                />
              ),
              p: ({ node, ...props }) => (
                <p {...props} className="text-primary leading-relaxed text-lg mb-4" style={{ textAlign: 'justify' }} />
              )
            }}
          >
            {story.content}
          </ReactMarkdown>
        </div>
      );
    }
    
    // For highlighted reading, use proper word mapping for synchronization
    const parts = story.content.split(/(\!\[.*?\]\(.*?\)|\([\/]?objects\/uploads\/[^)]+\))/);
    let displayWordCounter = 0;
    
    return (
      <div className="text-primary leading-relaxed text-lg prose prose-lg max-w-none">
        {parts.map((part, partIndex) => {
          // Check if this part is a standard markdown image
          const imageMatch = part.match(/\!\[(.*?)\]\((.*?)\)/);
          if (imageMatch) {
            return (
              <img
                key={partIndex}
                src={imageMatch[2]}
                alt={imageMatch[1]}
                className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg block"
              />
            );
          }
          
          // Check if this part is a path-only image format used in stories
          const pathMatch = part.match(/\(([\/]?objects\/uploads\/[^)]+)\)/);
          if (pathMatch) {
            return (
              <img
                key={partIndex}
                src={pathMatch[1]}
                alt="Story illustration"
                className="w-full max-w-md mx-auto my-6 rounded-lg shadow-lg block"
              />
            );
          }
          
          // Handle text content with word highlighting
          const words = part.trim().split(/\s+/).filter(word => word.length > 0);
          return (
            <p key={partIndex} className="text-primary leading-relaxed text-lg mb-4" style={{ textAlign: 'justify' }}>
              {words.map((word, wordIndex) => {
                const globalDisplayWordIndex = displayWordCounter;
                
                // Use word mapping to find if this display word corresponds to current audio word
                // We only reach this code when audioText exists (see gating condition above)
                const isCurrentWord = highlightingEnabled && wordMapping.length > 0 && wordMapping[currentWordIndex] === globalDisplayWordIndex;
                
                // Map saved audio word index to display word index for accurate bookmark placement
                const savedDisplayWordIndex = savedWordIndex >= 0 ? wordMapping[savedWordIndex] : -1;
                const isSavedPosition = globalDisplayWordIndex === savedDisplayWordIndex && savedDisplayWordIndex >= 0 && !isCurrentWord;
                
                displayWordCounter++;
                
                return (
                  <span
                    key={wordIndex}
                    data-word-index={globalDisplayWordIndex}
                    className={`relative transition-all duration-200 ${
                      isCurrentWord
                        ? 'bg-purple-200 px-1 rounded shadow-sm'
                        : isSavedPosition
                        ? 'bg-teal-100 px-1 rounded border-b-2 border-teal-400'
                        : ''
                    }`}
                  >
                    {isSavedPosition && (
                      <Bookmark className="w-3 h-3 text-teal-600 absolute -top-1 -right-1" />
                    )}
                    {word}{' '}
                  </span>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </>
    );
  }
  
  if (!story) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-4">Story not found</h1>
            <Link href="/stories">
              <Button variant="gradient" className="text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Stories
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      {/* Navigation */}
      <div className="border-b border-purple-100 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link href="/stories">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Stories
              </Button>
            </Link>
            
            <div className="flex items-center gap-2">
              <CommunityShareButton 
                storyId={story.id} 
                isOwner={user?.id === story.userId} 
              />
              
              <ShareStory story={story} />
              
              <Link href={`/vr/story/${story.id}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  title="View in VR"
                >
                  <Glasses className="w-4 h-4" />
                </Button>
              </Link>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleFavoriteMutation.mutate(story.id)}
              >
                <Heart 
                  className={`w-4 h-4 ${favoriteIds.includes(story.id) ? 'fill-purple-500 text-purple-500' : ''}`}
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Story Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="mb-8">
          <CardContent className="p-8">
            {/* Story Header */}
            <div className="mb-6">
              {editingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        updateTitleMutation.mutate({ id: story.id, title: titleValue });
                      } else if (e.key === 'Escape') {
                        setEditingTitle(false);
                        setTitleValue(story.title || 'My Bedtime Story');
                      }
                    }}
                    className="flex-1 text-3xl font-bold text-primary bg-transparent border-b-2 border-primary focus:outline-none"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={() => updateTitleMutation.mutate({ id: story.id, title: titleValue })}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingTitle(false);
                      setTitleValue(story.title || 'My Bedtime Story');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-bold text-primary">
                    {story.title || 'My Bedtime Story'}
                  </h1>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingTitle(true)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex items-center gap-4 mt-2 text-sm text-secondary">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(story.createdAt).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {Math.ceil(story.content.split(' ').length / 200)} min read
                </div>
                <span className="capitalize px-2 py-1 bg-accent/10 text-secondary rounded-full text-xs">
                  {story.style}
                </span>
              </div>
            </div>
            
            {/* Share to Community Button - Top */}
            <div className="mb-4 flex justify-center">
              <CommunityShareButton 
                storyId={story.id} 
                isOwner={user?.id === story.userId} 
              />
            </div>
            
            {/* PDF Book Downloads */}
            <div className="mb-8">
              <PdfBookDownload 
                storyId={story.id} 
                storyTitle={story.title || 'My Bedtime Story'} 
              />
            </div>

            
            {/* Reading Progress Indicator */}
            {readingProgress?.progress && !isRestoringProgress && (
              <div className="mb-4">
                <Card className="bg-gradient-to-r from-purple-50 to-teal-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Bookmark className="w-5 h-5 text-purple-600" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">Continue Reading</h3>
                        <p className="text-xs text-gray-600">
                          Resume from where you left off • {Math.round(readingProgress.progress.scrollPercentage)}% completed
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="gradient"
                        className="text-white text-xs"
                        data-testid="button-resume-reading"
                        onClick={async () => {
                          const progress = readingProgress.progress;
                          
                          // Restore word position for highlighting
                          if (progress.wordIndex >= 0) {
                            setCurrentWordIndex(progress.wordIndex);
                          }
                          
                          // Use improved scroll restoration
                          const success = await restoreScrollPosition(progress.scrollPercentage);
                          
                          if (success) {
                            setScrollPosition(progress.scrollPercentage);
                            toast({
                              title: "Reading position restored",
                              description: `Scrolled to ${Math.round(progress.scrollPercentage)}% of story`,
                              duration: 2000,
                            });
                          } else {
                            toast({
                              title: "Could not restore exact position",
                              description: "Using approximate position instead",
                              variant: "default",
                              duration: 3000,
                            });
                          }
                        }}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Resume
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Audio Controls */}
            <div className="mb-6 space-y-4">
              <div className="flex justify-center">
                <button
                  onClick={togglePlayPause}
                  disabled={isLoadingAudio}
                  className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-600 to-teal-600 text-purple-50 rounded-full hover:shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-teal-700 focus:outline-none focus:ring-4 focus:ring-purple-600 disabled:from-gray-400 disabled:to-gray-500"
                >
                  {isLoadingAudio ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Generating Audio...</span>
                    </>
                  ) : isPlaying ? (
                    <>
                      <Pause className="w-5 h-5" />
                      <span>Pause Story</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      <span>Listen to Story</span>
                    </>
                  )}
                  <Volume2 className="w-5 h-5" />
                </button>
              </div>
              
              {/* Audio Settings */}
              {(audioUrl || isPlaying) && (
                <div className="flex justify-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                    <input
                      type="checkbox"
                      checked={highlightingEnabled}
                      onChange={(e) => setHighlightingEnabled(e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-800">Highlight words</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                    <input
                      type="checkbox"
                      checked={soundEffectsEnabled}
                      onChange={(e) => setSoundEffectsEnabled(e.target.checked)}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <Music className="w-4 h-4 text-teal-600" />
                    <span className="text-sm text-gray-800">Sound effects</span>
                  </label>
                  
                  <button
                    onClick={() => setIsVoiceDialogOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-teal-100 hover:from-purple-200 hover:to-teal-200 rounded-full transition-colors"
                    data-testid="button-voice-settings"
                  >
                    <Mic className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-gray-800 font-medium">Voice Settings</span>
                  </button>
                </div>
              )}
            </div>
            
            {/* Story Text */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border-l-4 border-accent">
                {renderHighlightedStory()}
              </div>
            </div>
            
            {/* Share to Community Button - Bottom */}
            <div className="mt-6 flex justify-center">
              <CommunityShareButton 
                storyId={story.id} 
                isOwner={user?.id === story.userId} 
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Sticker Generator */}
        <div className="mb-8">
          <StickerGenerator
            storyId={story.id}
            storyTitle={story.title || 'My Bedtime Story'}
          />
        </div>
        
        {/* Merchandise Catalog */}
        <div className="mb-8">
          <MerchandiseCatalog
            storyId={story.id}
            storyTitle={story.title || 'My Bedtime Story'}
          />
        </div>
        
        {/* Print Book Order - At the end of the story */}
        <div className="mb-8">
          <PrintBookOrder 
            storyId={story.id} 
            storyTitle={story.title || 'My Bedtime Story'} 
          />
        </div>
      </div>
      
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => {
          const audio = e.currentTarget;
          setAudioDuration(audio.duration);
          
          if (highlightingEnabled && audioText) {
            // Guard against invalid duration (NaN, 0, or negative)
            if (!isFinite(audio.duration) || audio.duration <= 0) return;
            
            // Clamp progress ratio between 0 and 1
            const ratio = Math.min(1, Math.max(0, audio.currentTime / audio.duration));
            const words = audioText.split(/\s+/).filter(w => w.length > 0);
            
            // Clamp word index to valid range [0, words.length - 1]
            const estimatedWordIndex = Math.max(0, Math.min(words.length - 1, Math.floor(ratio * words.length)));
            setCurrentWordIndex(estimatedWordIndex);
            
            // Sound effects are currently disabled
            // if (soundEffectsEnabled) {
            //   const shouldPlaySound = shouldTriggerSound(
            //     estimatedWordIndex,
            //     lastSoundIndex,
            //     storyMoments
            //   );
            //   
            //   if (shouldPlaySound) {
            //     const moment = storyMoments.find(m => m.wordIndex === estimatedWordIndex);
            //     if (moment) {
            //       // Sound effects system is disabled
            //       setLastSoundIndex(estimatedWordIndex);
            //     }
            //   }
            // }
          }
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentWordIndex(-1);
          setLastSoundIndex(-10);
        }}
      />
      
      {/* Voice Assignment Dialog */}
      {story && (
        <VoiceAssignmentDialog
          open={isVoiceDialogOpen}
          onOpenChange={setIsVoiceDialogOpen}
          storyId={story.id}
          storyContent={story.content || ''}
          currentCharacterMap={story.characterMap || undefined}
        />
      )}
    </div>
    </>
  );
}